import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { PopupButton } from "react-calendly";
import Dashboard from "@/Dashboard";
import Portal, { LoginPage, AuthCallback } from "@/Portal";
import DesignStudio from "@/DesignStudio";
import DesireStudioPreview from "@/DesireStudioPreview";
import CEOField from "@/CEOField";
import FieldInspection from "@/FieldInspection";
import PartnerWelcome from "@/PartnerWelcome";
import PartnerIntake from "@/PartnerIntake";
import ClearUnitRestart from "@/ClearUnitRestart";
import RenderHub from "@/components/renderhub/RenderHub";
import Gateway from "@/Gateway";
import ExpoLive from "@/ExpoLive";
import CrewMobile from "@/CrewMobile";
import FWAA from "@/FWAA";
import FWAQR from "@/FWAQR";
import MyQR from "@/MyQR";
import CrewPortal from "@/Crew";
import FieldMode from "@/FieldMode";
import VendorConsent from "@/VendorConsent";
import SignContract from "@/SignContract";
import OwnerTriggerView from "@/OwnerTriggerView";
import VAContracts from "@/VAContracts";
import RequestPacket from "@/RequestPacket";
import VendorDisclaimer from "@/VendorDisclaimer";
import VendorApplication from "@/VendorApplication";
import OwnerProposal from "@/OwnerProposal";
import ActivationGate from "@/ActivationGate";
import OwnerShieldVault from "@/OwnerShieldVault";
import FieldSpecialist from "@/FieldSpecialist";
import VendorPortal from "@/VendorPortal";
import DesignStudioPreview from "@/DesignStudioPreview";
import LockdownStatusPage from "@/LockdownStatusPage";
import PipelineSimulation from "@/PipelineSimulation";
import GetAppPage from "@/GetAppPage";
import CooCommandCenter from "@/CooCommandCenter";
import MobileFieldNav from "./components/MobileFieldNav";
import RosterAdmin from "@/RosterAdmin";
import IdentityGate from "./components/IdentityGate";
import VacancyCostSlider from "./components/VacancyCostSlider";
import SiteErrorBoundary from "@/SiteErrorBoundary";
import { Toaster } from "sonner";
import ScopeLockRender from "./components/dashboard/ScopeLockRender";
import InstallPrompt from "./components/InstallPrompt";
import OnsiteGuidedWalkthrough from "./components/OnsiteGuidedWalkthrough";

const BACKEND_URL = "";
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com";
const PHONE_NUMBER = "(817) 381-8831";
const TEXT_NUMBER = "8173818831";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c45d5dc5-1aa9-4fda-8179-43802ae25660/artifacts/r7bevoxt_1210928841187418.jpg";

// Language context
const LangContext = React.createContext("en");
const useLang = () => React.useContext(LangContext);

const T = {
  nav_home: { en: "Home", es: "Inicio" },
  nav_calc: { en: "Calculator", es: "Calculadora" },
  nav_services: { en: "Services", es: "Servicios" },
  nav_contact: { en: "Contact", es: "Contacto" },
  hero_eyebrow: { en: "INSTITUTIONAL INTERIOR STABILIZATION · DFW METROPLEX", es: "ESTABILIZACIÓN INTERIOR INSTITUCIONAL · METROPLEX DFW" },
  hero_h1a: { en: "113,000 units sitting offline.", es: "113,000 unidades fuera de servicio." },
  hero_h1b: { en: "Institutional interior stabilization for DFW multifamily portfolios.", es: "Estabilización interior institucional para portafolios multifamiliares en DFW." },
  hero_sub: { en: "Institutional interior stabilization for DFW multifamily portfolios. Executive-grade delivery. Full engagement architecture disclosed upon walk-through booking.", es: "Estabilización interior institucional para portafolios multifamiliares en DFW. Entrega de grado ejecutivo. Arquitectura de compromiso completa divulgada al reservar la visita." },
  hero_cta: { en: "Book a Free Walk-Through", es: "Programe una Visita Gratuita" },
  hero_call: { en: "Call", es: "Llamar" },
  stat_avg: { en: "AVG STABILIZATION VS 45-DAY MARKET", es: "ESTABILIZACIÓN PROMEDIO VS 45 DÍAS DEL MERCADO" },
  stat_swift: { en: "SWIFT TURN CAPABILITY", es: "CAPACIDAD DE RENOVACIÓN RÁPIDA" },
  stat_fees: { en: "AGENT OR MANAGEMENT FEES", es: "COMISIONES DE AGENTE O ADMINISTRACIÓN" },
  value_h2: { en: "What Is Vacancy Costing Your NOI?", es: "¿Cuánto Le Cuesta la Vacancia a su NOI?" },
  value_label_units: { en: "Vacant Units", es: "Unidades Vacantes" },
  value_label_rent: { en: "Monthly Rent", es: "Renta Mensual" },
  value_label_days: { en: "Days Vacant", es: "Días Vacantes" },
  value_result_total: { en: "Total Vacancy Loss", es: "Pérdida Total por Vacancia" },
  value_result_daily: { en: "per day", es: "por día" },
  value_result_saved: { en: "You Could Save", es: "Podría Ahorrar" },
  value_result_note: { en: "by compressing turn time to 18 days", es: "al reducir el tiempo de renovación a 18 días" },
  tiers_h2: { en: "Three Ways We Stabilize Your Asset", es: "Tres Formas de Estabilizar Su Activo" },
  tier_swift: { en: "Swift Turn", es: "Renovación Rápida" },
  tier_swift_desc: { en: "Rapid Physical Recovery. Specialized execution of surface touch-ups, hardware modernization, and deep sanitation. Optimized to meet NSPIRE, HUD, and City Code physical standards — unit returns to the rent roll without delay.", es: "Recuperación Física Rápida. Ejecución especializada de retoques de superficie, modernización de herrajes y sanitización profunda. Optimizada para cumplir con los estándares físicos de NSPIRE, HUD y el Código de la Ciudad." },
  tier_standard: { en: "Standard Stabilization", es: "Estabilización Estándar" },
  tier_standard_desc: { en: "Interior Performance Optimization. Comprehensive surfacing turnover featuring high-durability flooring and fixture upgrades. Physical prep delivered Section 8, HUD-VASH, and Coalition-compliant for maximum market readiness.", es: "Optimización de Rendimiento Interior. Renovación integral de superficies con pisos de alta durabilidad y actualización de accesorios. Preparación física entregada conforme a Section 8, HUD-VASH y Coalition para máxima preparación de mercado." },
  tier_heavy: { en: "Heavy Recovery", es: "Recuperación Mayor" },
  tier_heavy_desc: { en: "Distressed Asset Physical Compliance. Strategic restoration for long-term offline units. Includes debris removal and full fixture replacement — transforms non-performing liabilities into inspection-ready assets.", es: "Remediación Física de Activos en Dificultad. Restauración estratégica para unidades inactivas a largo plazo. Incluye retiro de escombros y reemplazo completo de accesorios — transforma pasivos improductivos en activos listos para inspección." },
  tier_admin: { en: "Inspection Prep", es: "Preparación de Inspección" },
  tier_admin_desc: { en: "Administrative Add-On. Technical document preparation and scheduling assistance for Section 8 and housing coalitions. We handle paperwork logistics and inspection coordination to ensure your physical asset meets agency standards for move-in.", es: "Complemento Administrativo. Preparación de documentación técnica y coordinación de inspecciones para Section 8 y coaliciones de vivienda. Gestionamos la logística de papeleo e inspección para garantizar que su activo cumpla los estándares de agencia." },
  tier_most_common: { en: "Most Common", es: "Más Común" },
  tier_addon_label: { en: "Add-On Service", es: "Servicio Complementario" },
  tier_cta: { en: "Book a Walk-Through", es: "Programe una Visita" },
  tiers_note: { en: "Every engagement is executed to institutional documentation standards. Per-unit stabilization fee locked at walk-through — full delivery packet disclosed under engagement.", es: "Cada compromiso se ejecuta bajo estándares institucionales de documentación. Tarifa de estabilización por unidad fijada en la visita — paquete de entrega completo divulgado bajo compromiso." },
  trust_h2: { en: "Why Property Managers Trust Us", es: "Por Qué Los Administradores Confían en Nosotros" },
  contact_h2: { en: "Book Your Free Walk-Through", es: "Programe Su Visita Gratuita" },
  contact_sub: { en: "We'll assess your property and deliver a scope with exact costs — no obligation.", es: "Evaluaremos su propiedad y le entregaremos un presupuesto con costos exactos — sin compromiso." },
  contact_name: { en: "Full Name", es: "Nombre Completo" },
  contact_email: { en: "Email", es: "Correo Electrónico" },
  contact_phone: { en: "Phone", es: "Teléfono" },
  contact_message: { en: "Tell us about your property", es: "Cuéntenos sobre su propiedad" },
  contact_submit: { en: "Request Walk-Through", es: "Solicitar Visita" },
  contact_success: { en: "Received! We'll reach out within 24 hours.", es: "¡Recibido! Nos comunicaremos dentro de 24 horas." },
};

// Scroll reveal hook
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('revealed'); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
};

// Navigation
const Navigation = ({ lang, setLang }) => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <nav className="nav-container" data-testid="navigation">
      <div className="nav-content">
        <div className="nav-logo">
          <img src={LOGO_URL} alt="Moovin Forward" className="logo-image" data-testid="logo-image" />
        </div>
        <div className="nav-links">
          <button onClick={() => scrollToSection('hero')} className="nav-link" data-testid="nav-home">{T.nav_home[lang]}</button>
          <button onClick={() => scrollToSection('value')} className="nav-link" data-testid="nav-value">{T.nav_calc[lang]}</button>
          <button onClick={() => scrollToSection('services')} className="nav-link" data-testid="nav-services">{T.nav_services[lang]}</button>
          <button onClick={() => scrollToSection('contact')} className="nav-link" data-testid="nav-contact">{T.nav_contact[lang]}</button>
          <div className="nav-lang-toggle" data-testid="lang-toggle">
            <button onClick={() => setLang("en")} className={`nav-lang-btn ${lang === "en" ? "active" : ""}`}>EN</button>
            <button onClick={() => setLang("es")} className={`nav-lang-btn ${lang === "es" ? "active" : ""}`}>ES</button>
          </div>
        </div>
        <div className="nav-right">
          <Link to="/login" className="nav-portal-btn" data-testid="nav-client-login" title="Sign in to your Client Portal or Team Dashboard">Sign In</Link>
          <a href={`tel:${PHONE_NUMBER}`} className="nav-phone" data-testid="nav-phone">{PHONE_NUMBER}</a>
          <a href={`sms:${TEXT_NUMBER}`} className="nav-text-btn" data-testid="nav-text-btn">Text Us</a>
          <PopupButton url={CALENDLY_URL} rootElement={document.getElementById("root")} text="Book Walk-Through" className="nav-cta" data-testid="nav-cta-btn" />
        </div>
      </div>
    </nav>
  );
};

// Section 1: Hero — The 113,000-Unit Crisis
const HeroSection = () => {
  const lang = useLang();
  return (
  <section id="hero" className="hero-section" data-testid="hero-section">
    <div className="hero-inner">
      <p className="hero-tag" data-testid="hero-tag">{T.hero_eyebrow[lang]}</p>
      <h1 className="hero-h1" data-testid="hero-h1">
        {T.hero_h1a[lang]}<br />
        <span className="hero-accent">{T.hero_h1b[lang]}</span>
      </h1>
      <p className="hero-sub">
        {T.hero_sub[lang]}
      </p>
      <div className="hero-actions">
        <PopupButton url={CALENDLY_URL} rootElement={document.getElementById("root")} text={T.hero_cta[lang]} className="btn-primary-lg" data-testid="hero-cta-btn" />
        <a href={`tel:${PHONE_NUMBER}`} className="btn-outline-lg" data-testid="hero-call-btn">{T.hero_call[lang]} {PHONE_NUMBER}</a>
      </div>
      <div style={{ maxWidth: 560, marginTop: 24 }}>
        <IdentityGate />
      </div>
      <VacancyCostSlider />
      <div className="hero-proof-bar" data-testid="hero-proof-bar">
        <div className="proof-item"><span className="proof-val">18 Days</span><span className="proof-label">{T.stat_avg[lang]}</span></div>
        <div className="proof-divider"></div>
        <div className="proof-item"><span className="proof-val">48 hrs</span><span className="proof-label">{T.stat_swift[lang]}</span></div>
        <div className="proof-divider"></div>
        <div className="proof-item"><span className="proof-val">$0</span><span className="proof-label">{T.stat_fees[lang]}</span></div>
      </div>
    </div>
  </section>
  );
};

// Section 2: The Value — Problem/Solution + Calculator (Calculator first on mobile)
const ValueSection = () => {
  const ref = useReveal();
  const [rent, setRent] = useState(2189);
  const dailyCost = (rent / 30).toFixed(0);
  const weeklyCost = (rent / 4.3).toFixed(0);
  const day45Cost = (rent * 1.5).toFixed(0);
  const savedWithUs = (rent * 0.8).toFixed(0);

  return (
    <section id="value" className="value-section reveal-section" ref={ref} data-testid="value-section">
      <div className="section-inner">
        <div className="value-grid">
          <div className="value-left">
            <h2 className="value-h2">Every empty day erodes your bottom line.</h2>
            <p className="value-desc">The average DFW vacancy <strong>drains $3,285+</strong> in lost revenue prior to stabilization. Quantify your portfolio exposure.</p>
          </div>
          <div className="value-right">
            <div className="calc-card" data-testid="calculator">
              <h3 className="calc-title">Vacancy Cost Analysis</h3>
              <div className="calc-input-row">
                <label>Monthly Rent</label>
                <div className="calc-input-wrap">
                  <span>$</span>
                  <input type="number" value={rent} onChange={(e) => setRent(Number(e.target.value) || 0)} min="0" max="10000" data-testid="rent-input" />
                </div>
                <input type="range" min="500" max="5000" step="50" value={rent} onChange={(e) => setRent(Number(e.target.value))} className="calc-slider" data-testid="rent-slider" />
              </div>
              <div className="calc-results">
                <div className="calc-stat"><span className="calc-stat-val">${dailyCost}</span><span className="calc-stat-lbl">Lost / Day</span></div>
                <div className="calc-stat"><span className="calc-stat-val">${weeklyCost}</span><span className="calc-stat-lbl">Lost / Week</span></div>
                <div className="calc-stat danger"><span className="calc-stat-val">${day45Cost}</span><span className="calc-stat-lbl">45-Day Loss</span></div>
                <div className="calc-stat success"><span className="calc-stat-val">~${savedWithUs}</span><span className="calc-stat-lbl">Recoverable Loss</span></div>
              </div>
              <PopupButton url={CALENDLY_URL} rootElement={document.getElementById("root")} text="Request Custom Engagement" className="btn-primary calc-cta" data-testid="calc-cta-btn" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Tax Shield Calculator — Curiosity-gated, lead-capture-first
const TaxShieldSection = () => {
  const ref = useReveal();
  const [units, setUnits] = useState(5);
  const [spendPerUnit, setSpendPerUnit] = useState(8000);

  // Lead capture state
  const [briefForm, setBriefForm] = useState({ name: "", email: "", company: "", units: "", role: "" });
  const [briefStatus, setBriefStatus] = useState("idle"); // idle | submitting | success | error
  const [briefError, setBriefError] = useState("");

  const totalDeployment = units * spendPerUnit;
  // Section 168(k) 100% bonus depreciation savings range (directional only):
  const savingsLow = Math.round(totalDeployment * 0.20);
  const savingsHigh = Math.round(totalDeployment * 0.35);
  const savingsMid = Math.round(totalDeployment * 0.275);
  const netCost = totalDeployment - savingsMid;

  const fmt = (n) => n.toLocaleString('en-US');

  const submitBrief = async (e) => {
    e.preventDefault();
    if (!briefForm.email || !briefForm.name) {
      setBriefError("Name and email are required.");
      setBriefStatus("error");
      return;
    }
    setBriefStatus("submitting");
    setBriefError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/tax-brief/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: briefForm.name.trim(),
          email: briefForm.email.trim().toLowerCase(),
          company: briefForm.company.trim(),
          portfolio_units: briefForm.units.trim(),
          role: briefForm.role.trim(),
          source: "landing_tax_shield"
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Submission failed");
      }
      setBriefStatus("success");
    } catch (err) {
      setBriefError(err.message || "Something went wrong. Please try again.");
      setBriefStatus("error");
    }
  };

  return (
    <section id="tax-shield" className="tax-shield-section reveal-section" ref={ref} data-testid="tax-shield-section">
      <div className="section-inner">
        <div className="tax-shield-grid">
          <div className="tax-shield-left">
            <p className="tax-shield-eyebrow" data-testid="tax-shield-eyebrow">For Asset Owners &amp; Portfolio Managers</p>
            <h2 className="tax-shield-h2">We don&rsquo;t just renovate.<br /><span className="tax-shield-accent">We engineer tax advantages.</span></h2>
            <p className="tax-shield-desc">
              Our institutional clients realize meaningful Year-1 tax efficiency through a stabilization model engineered for both <strong>physical asset recovery</strong> and <strong>maximum capital deployment efficiency</strong>. The directional estimate to the right shows the order of magnitude. The full strategy is shared privately with qualified owners.
            </p>

            {briefStatus !== "success" ? (
              <form className="tax-brief-form" onSubmit={submitBrief} data-testid="tax-brief-form">
                <p className="tax-brief-form-title">Ask Us How.</p>
                <p className="tax-brief-form-sub">A confidential note from Shannon, Founder &mdash; not a newsletter, not a sales blast. One conversation, one tailored answer.</p>

                <div className="tax-brief-row">
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={briefForm.name}
                    onChange={(e) => setBriefForm({ ...briefForm, name: e.target.value })}
                    required
                    data-testid="brief-input-name"
                  />
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={briefForm.email}
                    onChange={(e) => setBriefForm({ ...briefForm, email: e.target.value })}
                    required
                    data-testid="brief-input-email"
                  />
                </div>
                <div className="tax-brief-row">
                  <input
                    type="text"
                    placeholder="Company / Portfolio"
                    value={briefForm.company}
                    onChange={(e) => setBriefForm({ ...briefForm, company: e.target.value })}
                    data-testid="brief-input-company"
                  />
                  <select
                    value={briefForm.role}
                    onChange={(e) => setBriefForm({ ...briefForm, role: e.target.value })}
                    data-testid="brief-input-role"
                  >
                    <option value="">Your Role</option>
                    <option value="Owner / Investor">Owner / Investor</option>
                    <option value="Property Manager">Property Manager</option>
                    <option value="REIT / Institutional">REIT / Institutional</option>
                    <option value="Housing Authority / Agency">Housing Authority / Agency</option>
                    <option value="Broker">Broker</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="tax-brief-row">
                  <input
                    type="text"
                    placeholder="Approx. units in your portfolio"
                    value={briefForm.units}
                    onChange={(e) => setBriefForm({ ...briefForm, units: e.target.value })}
                    data-testid="brief-input-units"
                  />
                </div>

                {briefStatus === "error" && (
                  <p className="tax-brief-error" data-testid="brief-error">{briefError}</p>
                )}

                <button
                  type="submit"
                  className="tax-brief-submit"
                  disabled={briefStatus === "submitting"}
                  data-testid="brief-submit-btn"
                >
                  {briefStatus === "submitting" ? "Sending..." : "Ask Us How →"}
                </button>

                <p className="tax-brief-privacy">
                  We use this only to send the brief and to follow up with you directly. Never sold, never shared.
                </p>
              </form>
            ) : (
              <div className="tax-brief-success" data-testid="brief-success">
                <h3>Thank you.</h3>
                <p>The Asset Stabilization Brief is on its way to <strong>{briefForm.email}</strong>. Shannon will follow up personally within one business day.</p>
              </div>
            )}
          </div>

          <div className="tax-shield-right">
            <div className="tax-calc-card" data-testid="tax-calculator" style={{ position: "relative", overflow: "hidden" }}>
              <h3 className="tax-calc-title">Directional Year-1 Estimate</h3>

              {/* Locked-vault overlay — full calculator disclosed after Sign In */}
              <div data-testid="tax-calc-lock" style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(160deg, rgba(2,10,31,0.86), rgba(2,10,31,0.96))",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                display: "flex", flexDirection: "column", justifyContent: "center",
                alignItems: "center", padding: "clamp(20px,3vw,32px)", textAlign: "center",
                border: "1px solid rgba(0,207,207,0.35)", borderRadius: 12, zIndex: 5,
              }}>
                <div style={{ fontSize: 10, letterSpacing: "0.25em", color: "#00CFCF", marginBottom: 10 }}>
                  EXECUTIVE CAPEX CALCULATOR
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#F5F5F7", marginBottom: 10, lineHeight: 1.25 }}>
                  Year-1 Tax Advantage · Locked
                </div>
                <p style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 340, color: "#A0A0A0", marginBottom: 18 }}>
                  Full CapEx model with per-unit spend inputs, conservative and high-end depreciation estimates, and net-cost projection is available to institutional asset holders after sign-in or walk-through booking.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                  <Link to="/login" data-testid="tax-calc-signin-cta" style={{
                    display: "inline-block", padding: "10px 20px", background: "#00CFCF", color: "#020A1F",
                    borderRadius: 999, fontWeight: 700, textDecoration: "none", fontSize: 13, letterSpacing: "0.02em",
                  }}>Sign In &rarr;</Link>
                  <a href="#contact" data-testid="tax-calc-book-cta" style={{
                    display: "inline-block", padding: "10px 20px", background: "transparent", color: "#F5F5F7",
                    border: "1px solid rgba(160,160,160,0.5)", borderRadius: 999, fontWeight: 700,
                    textDecoration: "none", fontSize: 13, letterSpacing: "0.02em",
                  }}>Book Walk-Through</a>
                </div>
              </div>

              {/* Blurred content preview underneath (kept for silhouette + tests) */}
              <div style={{ filter: "blur(3px)", opacity: 0.5, pointerEvents: "none", userSelect: "none" }} aria-hidden="true">
              <div className="tax-calc-row">
                <label>Number of Units</label>
                <div className="tax-calc-input-wrap">
                  <input type="number" value={units} onChange={(e) => setUnits(Math.max(1, Math.min(500, Number(e.target.value) || 1)))} min="1" max="500" data-testid="tax-units-input" readOnly />
                </div>
                <input type="range" min="1" max="50" step="1" value={units > 50 ? 50 : units} onChange={(e) => setUnits(Number(e.target.value))} className="tax-calc-slider" data-testid="tax-units-slider" readOnly />
              </div>

              <div className="tax-calc-row">
                <label>Avg Spend Per Unit</label>
                <div className="tax-calc-input-wrap">
                  <span>$</span>
                  <input type="number" value={spendPerUnit} onChange={(e) => setSpendPerUnit(Math.max(0, Number(e.target.value) || 0))} min="0" max="100000" step="500" data-testid="tax-spend-input" readOnly />
                </div>
                <input type="range" min="2000" max="25000" step="500" value={spendPerUnit > 25000 ? 25000 : spendPerUnit} onChange={(e) => setSpendPerUnit(Number(e.target.value))} className="tax-calc-slider" data-testid="tax-spend-slider" readOnly />
              </div>

              <div className="tax-calc-deployment" data-testid="tax-deployment">
                <span className="tax-calc-deployment-lbl">Total Capital Deployment</span>
                <span className="tax-calc-deployment-val">$ &mdash; &mdash; &mdash;</span>
              </div>

              <div className="tax-calc-results">
                <div className="tax-calc-stat" data-testid="tax-savings-low">
                  <span className="tax-calc-stat-val">$ &mdash; &mdash;</span>
                  <span className="tax-calc-stat-lbl">Conservative</span>
                </div>
                <div className="tax-calc-stat tax-calc-stat-high" data-testid="tax-savings-high">
                  <span className="tax-calc-stat-val">$ &mdash; &mdash;</span>
                  <span className="tax-calc-stat-lbl">High End</span>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Curiosity Bridge — 3 steps that tease the solution before revealing pricing
const CuriosityBridge = () => {
  const ref = useReveal();
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <section className="curiosity-section reveal-section" ref={ref} data-testid="curiosity-bridge">
      <div className="section-inner">
        <p className="curiosity-eyebrow" data-testid="curiosity-eyebrow">So how do we stop the bleeding?</p>
        <h2 className="curiosity-h2" data-testid="curiosity-h2">Three steps. One team. Zero guesswork.</h2>
        <div className="curiosity-steps">
          <div className="curiosity-step" data-testid="curiosity-step-1">
            <div className="curiosity-num">1</div>
            <h3>We Walk It</h3>
            <p>Free on-site assessment. We scope every surface, every fixture, every detail — no commitment, no invoice.</p>
          </div>
          <div className="curiosity-connector"></div>
          <div className="curiosity-step" data-testid="curiosity-step-2">
            <div className="curiosity-num">2</div>
            <h3>You Green-Light It</h3>
            <p>Review the plan. Approve the finish specs. Fund your Activation Capital — every dollar goes directly into your property.</p>
          </div>
          <div className="curiosity-connector"></div>
          <div className="curiosity-step" data-testid="curiosity-step-3">
            <div className="curiosity-num">3</div>
            <h3>Keys Back in Hand</h3>
            <p>Turn-key delivery. Walk through. Approve. Done. Your Stabilization Fee is due only after you sign off.</p>
          </div>
        </div>
        <div className="curiosity-teaser" data-testid="curiosity-teaser">
          <button onClick={scrollToPricing} className="curiosity-reveal-btn" data-testid="curiosity-reveal-btn">
            What does this actually cost?
            <span className="curiosity-arrow">&#8595;</span>
          </button>
        </div>
      </div>
    </section>
  );
};

// Market Crisis — The 113,000 Legacy Unit Crisis
const MarketCrisisSection = () => {
  const ref = useReveal();
  return (
    <section id="about" className="market-crisis-section reveal-section" ref={ref} data-testid="market-crisis-section">
      <div className="section-inner">
        <p className="section-eyebrow">DFW Market Intelligence | Q2 2026</p>
        <h2 className="section-h2">113,000 Legacy Units. Zero Revenue.</h2>
        <p className="section-lead" data-testid="crisis-lead">DFW is at a 20-year vacancy high. This isn't one bad property — it's a regional backlog.</p>

        <div className="crisis-grid" data-testid="crisis-grid">
          <div className="crisis-card">
            <span className="crisis-stat">37,457</span>
            <h4>The Supply Surge</h4>
            <p>37K+ new units delivered in 12 months shifted leverage to renters. Legacy properties lose every day they sit idle.</p>
          </div>
          <div className="crisis-card">
            <span className="crisis-stat">$826M+</span>
            <h4>Distressed Loans</h4>
            <p>$826M+ in Texas multifamily loans headed to foreclosure auction in January 2026 alone. Dallas represents $280M+ of that.</p>
          </div>
          <div className="crisis-card">
            <span className="crisis-stat">11.7%</span>
            <h4>Vacancy — 20-Year High</h4>
            <p>DFW vacancy hit 11.7% in 2025. Every day offline = lost NOI + concession burn. Owners giving away 2-3 months free rent just to compete.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Service Model — PUBLIC-FACING TEASER
// Full delivery specifications, timeline windows, material logic, and
// compliance stack are disclosed to institutional clients under executive
// brief post walk-through. Public view keeps the value pull without
// exposing proprietary operational mechanics.
const ServiceModelSection = () => {
  const ref = useReveal();
  return (
    <section className="service-model-section reveal-section" ref={ref} data-testid="service-model-section">
      <div className="section-inner">
        <p className="section-eyebrow">The Precision Wave Strategy</p>
        <h2 className="section-h2">Rolling Asset Delivery. Continuous Momentum.</h2>
        <p className="section-lead" style={{ maxWidth: 720, margin: "12px auto 28px", textAlign: "center" }}>
          Three engagement tracks calibrated to portfolio velocity — swift refresh, volume stabilization, and heavy recovery. Each track ships fully documented, inspection-ready, and architected for continuous multi-unit throughput.
        </p>

        <div className="service-triple" data-testid="service-dual">
          <div className="service-track" data-testid="service-48hr">
            <div className="service-track-header service-track-express">
              <span className="service-track-badge">SWIFT TURN</span>
              <h3>Light Refresh Engagement</h3>
            </div>
            <p>Low-friction units returned to inspection-ready market status on an accelerated cadence. Ideal for high-turnover portfolios.</p>
          </div>

          <div className="service-track" data-testid="service-14day">
            <div className="service-track-header service-track-standard">
              <span className="service-track-badge">VOLUME</span>
              <h3>Standard Stabilization</h3>
            </div>
            <p>Our primary engagement bracket for multi-unit institutional requests. Continuous throughput with executive-grade documentation at handoff.</p>
          </div>

          <div className="service-track" data-testid="service-21day">
            <div className="service-track-header service-track-full">
              <span className="service-track-badge">HEAVY</span>
              <h3>Deep Recovery Engagement</h3>
            </div>
            <p>Legacy or long-offline asset restoration. Full interior transformation delivered at a fraction of traditional renovation costs.</p>
          </div>
        </div>

        {/* Executive gate — full spec disclosed at walk-through */}
        <div className="service-teaser-gate" data-testid="service-teaser-gate" style={{
          background: "linear-gradient(135deg, rgba(0,207,207,0.06), rgba(0,207,207,0.02))",
          border: "1px solid rgba(0,207,207,0.35)",
          borderRadius: 12,
          padding: "clamp(18px,3vw,28px)",
          marginTop: 28,
          textAlign: "center",
        }}>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#00CFCF", marginBottom: 8 }}>
            EXECUTIVE DELIVERY SPECIFICATIONS
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.55, maxWidth: 620, margin: "0 auto 16px", color: "#F5F5F7" }}>
            Full delivery timelines, per-track deliverable checklists, material engineering logic, and multi-agency compliance stack are disclosed under executive brief upon walk-through booking.
          </p>
          <a href="#contact" className="cta-primary" data-testid="service-teaser-cta" style={{
            display: "inline-block", padding: "11px 26px", background: "#00CFCF", color: "#020A1F",
            borderRadius: 999, fontWeight: 700, textDecoration: "none", letterSpacing: "0.02em",
          }}>
            Book Walk-Through &rarr;
          </a>
        </div>

        <div className="operational-note" data-testid="operational-note" style={{ marginTop: 28 }}>
          Every unit we touch is a home someone is walking into for the first time &mdash; a veteran returning from deployment, a survivor beginning again, a family carrying a housing voucher into a fresh chapter. Moovin Forward LLC delivers the interior with the dignity that meets them at the doorway. The property&rsquo;s on-site maintenance staff owns the exterior condition, and our compressed interior cadence is architected to close on the same day theirs does. Two workstreams. One market-ready property. One family walking home.
        </div>
      </div>
    </section>
  );
};

// Stakeholder Pillars — Who We Serve
const StakeholderSection = () => {
  const ref = useReveal();
  return (
    <section className="stakeholder-section reveal-section" ref={ref} data-testid="stakeholder-section">
      <div className="section-inner">
        <p className="section-eyebrow">Strategic Positioning for Stakeholders</p>
        <h2 className="section-h2">Not a Vendor. A Stabilization Partner.</h2>

        <div className="stakeholder-grid" data-testid="stakeholder-grid">
          <div className="stakeholder-card" data-testid="stakeholder-investors">
            <h3>Investors & REITs</h3>
            <p>Your current investment is competing against 113,000 available units. Every day offline erodes NOI.</p>
            <p>We position legacy properties for immediate occupancy using compliance-first systems and market-driven finish specs.</p>
            <span className="stakeholder-cta">ROI stabilized. NOI protected. Portfolio velocity accelerated.</span>
          </div>

          <div className="stakeholder-card" data-testid="stakeholder-brokers">
            <h3>Brokers — Disposition & Acquisition</h3>
            <p>Offline units kill valuations. We stabilize vacant inventory to increase Cap Rate before sale.</p>
            <p>Itemized Certification proves every unit is compliant and occupancy-ready — not just "painted."</p>
            <span className="stakeholder-cta">Higher Cap Rate. Faster close. Defensible due diligence.</span>
          </div>

          <div className="stakeholder-card" data-testid="stakeholder-receivers">
            <h3>Receiverships & Asset Recovery</h3>
            <p>Distressed assets in an 11.7% vacancy market need precision — not guesswork.</p>
            <ul className="service-list">
              <li><strong>Intricate Reporting:</strong> Full itemized breakdown per unit — matched to court-approved budgets.</li>
              <li><strong>Structured Payment Hedge:</strong> Delivery-anchored payment architecture — zero capital flight risk. Full mechanics disclosed at walk-through.</li>
            </ul>
            <span className="stakeholder-cta">Court-grade documentation. Zero capital flight risk.</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// 50/50 Model + Financial Shield + TREC Independence (Consolidated)
// PUBLIC-FACING VARIANT — operational mechanics gated behind walk-through booking.
// Full financial architecture, industry data, and payment stack disclosed only to
// engaged clients under executive brief (see /dashboard for authenticated view).
const PaymentModelSection = () => {
  const ref = useReveal();
  return (
    <section className="payment-model-section reveal-section" ref={ref} data-testid="payment-model-section">
      <div className="section-inner">
        <p className="section-eyebrow">The Trust Architecture</p>
        <h2 className="section-h2">Capital Deployed to Your Asset — Not to a Receivable.</h2>
        <p className="section-lead" data-testid="payment-lead">
          Our engagement structure eliminates the industry's payment friction. Your capital moves directly into stabilizing your property under a fully-auditable framework — never co-mingled, never delayed by internal requisition queues.
        </p>

        <div className="payment-teaser" data-testid="payment-teaser" style={{
          background: "linear-gradient(135deg, rgba(0,207,207,0.06), rgba(0,207,207,0.02))",
          border: "1px solid rgba(0,207,207,0.35)",
          borderRadius: 12,
          padding: "clamp(20px,3vw,32px)",
          marginTop: 24,
          textAlign: "center",
        }}>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#00CFCF", marginBottom: 10 }}>
            EXECUTIVE FINANCIAL ARCHITECTURE
          </div>
          <p style={{ fontSize: 16, lineHeight: 1.55, maxWidth: 640, margin: "0 auto 18px", color: "#F5F5F7" }}>
            The complete payment architecture, engagement mechanics, and industry-data justification are disclosed to institutional asset holders under executive brief at the walk-through stage.
          </p>
          <a href="#contact" className="cta-primary" data-testid="payment-teaser-cta"
             style={{ display: "inline-block", padding: "12px 28px", background: "#00CFCF", color: "#020A1F", borderRadius: 999, fontWeight: 700, textDecoration: "none", letterSpacing: "0.02em" }}>
            Book Walk-Through to Access Executive Brief &rarr;
          </a>
        </div>

        {/* TREC Independence — kept public (positioning, not mechanics) */}
        <div className="trec-banner" data-testid="trec-shield" style={{ marginTop: 28 }}>
          <div className="trec-banner-left">
            <span className="trec-shield-badge">Outside TREC Scope</span>
            <span className="trec-banner-title">Independent ROI Stabilizer</span>
          </div>
          <p className="trec-banner-text"><strong>We don&rsquo;t touch your leasing process.</strong> We hand you a unit that passes inspection on day one. You lease it. Your licensed PM agent does what they do. Zero administrative lag, 100% production focus.</p>
        </div>
      </div>
    </section>
  );
};

// Site Readiness Protocol — Compact (Landing Page)
const SiteReadinessSection = () => {
  const ref = useReveal();
  return (
    <section className="site-readiness-section reveal-section" ref={ref} data-testid="site-readiness-section">
      <div className="section-inner">
        <p className="section-eyebrow">Professional Standards</p>
        <h2 className="section-h2">Zero-Tolerance Site Readiness</h2>

        <div className="readiness-compact" data-testid="readiness-grid">
          <div className="readiness-card-wide" data-testid="readiness-pest">
            <h4>Habitability & Infestation Protocol</h4>
            <p>Client warrants pest-free status. Multi-unit properties require adjacent and vertically stacked units cleared within 60 days. All appliances removed prior to mobilization. Bed bugs are classified "Severe" under HUD NSPIRE — no exceptions.</p>
          </div>
          <div className="readiness-card-wide" data-testid="readiness-discovery">
            <h4>Discovery & Indemnification</h4>
            <p>Active infestation found mid-project triggers immediate work cessation. Texas-licensed SPCS treatment and certified clearance required before resumption. Client assumes liability for contamination of equipment and materials due to non-disclosure.</p>
          </div>
        </div>
        <p className="readiness-footer" data-testid="readiness-footer">Full protocol and digital acknowledgment in the Client Portal upon onboarding.</p>
      </div>
    </section>
  );
};

// Risk Management — Macro-Economic Hedge
const RiskHedgeSection = () => {
  const ref = useReveal();
  return (
    <section className="risk-hedge-section reveal-section" ref={ref} data-testid="risk-hedge-section">
      <div className="section-inner">
        <p className="section-eyebrow">Risk Management | 2024-2027 Volatility Gap</p>
        <h2 className="section-h2">Your Hedge Against Supply Chain Instability</h2>
        <p className="section-lead" data-testid="risk-lead">12.2% tariff volatility, 20-year energy highs, unpredictable freight — we deliver fixed-cost stability when the market won't.</p>

        <div className="risk-grid" data-testid="risk-grid">
          <div className="risk-card" data-testid="risk-tariff">
            <span className="risk-stat">12.2%</span>
            <h4>Tariff Volatility Spike</h4>
            <p>Proposed global tariffs pushing flooring, fixtures, and electronics to a 20-year pricing high.</p>
            <span className="risk-solution">We set Material Budget Caps. Your cost stays fixed.</span>
          </div>

          <div className="risk-card" data-testid="risk-fuel">
            <span className="risk-stat">$4.12</span>
            <h4>Fuel & Diesel Surcharges</h4>
            <p>Diesel at $4.12/gal avg in 2026 — up 20.1%. Every delivery to every job site costs more.</p>
            <span className="risk-solution">We absorb the logistical headache so your ROI remains predictable.</span>
          </div>

          <div className="risk-card" data-testid="risk-energy">
            <span className="risk-stat">20-Year</span>
            <h4>Energy Demand High</h4>
            <p>U.S. electricity at a 20-year peak. Vacant units bleed holding costs every day they sit offline.</p>
            <span className="risk-solution">Our Speed-to-Stabilization model counters rising holding costs by getting units leased faster.</span>
          </div>
        </div>

        <div className="risk-budget-example" data-testid="risk-budget-cap">
          <strong>Example:</strong> Our Material Budget Cap for a standard 2-bedroom is $1,100. Regardless of tariff spikes, fuel surcharges, or supply chain disruption — that cap holds. The owner's ROI stays predictable.
        </div>
      </div>
    </section>
  );
};

// Section 3: How It Works — 3 steps
const ProcessSection = () => {
  const ref = useReveal();
  return (
  <section id="process" className="process-section reveal-section" ref={ref} data-testid="process-section">
    <div className="section-inner">
      <h2 className="section-h2">How It Works</h2>
      <div className="steps-row">
        <div className="step" data-testid="step-0">
          <div className="step-num">1</div>
          <h3>Schedule</h3>
          <p>Book a free walk-through. We come to your property.</p>
        </div>
        <div className="step-arrow">{"\u2192"}</div>
        <div className="step" data-testid="step-1">
          <div className="step-num">2</div>
          <h3>Walk-Through</h3>
          <p>We assess the unit on-site and build your custom plan.</p>
        </div>
        <div className="step-arrow">{"\u2192"}</div>
        <div className="step" data-testid="step-2">
          <div className="step-num">3</div>
          <h3>We Execute</h3>
          <p>Updates and progress reporting. We keep you informed every step.</p>
        </div>
      </div>
    </div>
  </section>
  );
};

// Section 4: The Advantage — Turn-Key, Upgrades, Cost Efficiency + Results
const TrustSection = () => {
  const ref = useReveal();
  return (
  <section className="trust-section reveal-section" ref={ref} data-testid="trust-section">
    <div className="section-inner">
      <h2 className="section-h2">The Moovin Forward Advantage</h2>
      <div className="advantage-grid">
        <div className="advantage-card" data-testid="advantage-turnkey">
          <h3>Rent-Roll Delivery</h3>
          <p>Physical asset returned to the rent roll <strong>inspection-ready</strong>. Every surface addressed, documented, and signed off. One handoff — keys back, unit performing.</p>
        </div>
        <div className="advantage-card" data-testid="advantage-upgrades">
          <h3>Market-Ready Finishes</h3>
          <p>Modern, <strong>interchangeable design variations</strong> engineered for tenant yield — photograph clean for listings, hold up across lease cycles, and meet HUD / HUD-VASH physical standards.</p>
        </div>
        <div className="advantage-card" data-testid="advantage-cost">
          <h3>Cost-Efficient Capital Deployment</h3>
          <p><strong>Modular scope control</strong>. Scope-locked before work begins. Zero change orders. Single-source coordination eliminates subcontractor markup and compliance gaps.</p>
        </div>
      </div>
      <div className="results-bar" data-testid="results-bar">
        <div className="result-item"><span className="result-val">18 Days</span><span className="result-label">Avg Stabilization vs. 45-Day Market Avg</span></div>
        <div className="result-divider"></div>
        <div className="result-item"><span className="result-val">$0</span><span className="result-label">Broker, Locator, or Management Fees</span></div>
        <div className="result-divider"></div>
        <div className="result-item"><span className="result-val">100%</span><span className="result-label">NSPIRE-Compliant Documentation</span></div>
      </div>
    </div>
  </section>
  );
};

// Section: The Full Cost — Comparison (Email-Gated)
const ComparisonSection = () => {
  const ref = useReveal();
  const [unlocked, setUnlocked] = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [gateName, setGateName] = useState("");
  const [gateCompany, setGateCompany] = useState("");
  const [gateLoading, setGateLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("mf_report_unlocked")) setUnlocked(true);
  }, []);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!gateEmail) return;
    setGateLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/unlock-report`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: gateEmail, name: gateName, company: gateCompany })
      });
      localStorage.setItem("mf_report_unlocked", "true");
      setUnlocked(true);
    } catch(err) { console.error(err); }
    setGateLoading(false);
  };

  return (
  <section id="compare" className="comparison-section reveal-section" ref={ref} data-testid="comparison-section">
    <div className="section-inner">
      <h2 className="section-h2">The Full Cost — Not Just the Invoice</h2>
      <p className="section-lead" data-testid="compare-lead">The total cost of turnover — vacancy loss, management fees, placement fees, concessions — averages <strong>$3,976 per unit</strong> nationally.</p>

      {/* Hook — always visible */}
      <div className="compare-hook" data-testid="compare-hook">
        <div className="hook-card hook-old"><span className="hook-label">Traditional Vendor + Agent Stack</span><span className="hook-val hook-val-old">$8,760+</span><span className="hook-sub">Year 1 all-in cost per unit</span></div>
        <div className="hook-vs">vs</div>
        <div className="hook-card hook-new"><span className="hook-label">Moovin Forward</span><span className="hook-val hook-val-new">~$4,250</span><span className="hook-sub">Year 1 all-in cost per unit</span></div>
      </div>

      <div className="savings-bar" data-testid="savings-bar">
        <span className="savings-label">Owner saves</span>
        <span className="savings-val">$4,500+</span>
        <span className="savings-sub">per unit, per year — full control retained, zero TREC liability</span>
      </div>

      {/* Gated Detail */}
      {unlocked ? (
        <div className="comparison-detail" data-testid="comparison-detail">
          <h3 className="detail-title">Full Breakdown — Standard 2BR at $1,800/mo</h3>
          <div className="comparison-grid">
            <div className="compare-col compare-old" data-testid="compare-traditional">
              <h3 className="compare-heading compare-heading-old">Traditional</h3>
              <div className="compare-line"><span className="compare-label">Make-ready vendor</span><span className="compare-val compare-val-old">$750</span></div>
              <div className="compare-line"><span className="compare-label">Property manager (10%/yr)</span><span className="compare-val compare-val-old">$2,160</span></div>
              <div className="compare-line"><span className="compare-label">Locator/placement fee</span><span className="compare-val compare-val-old">$1,350</span></div>
              <div className="compare-line"><span className="compare-label">Vacancy loss (45 days)</span><span className="compare-val compare-val-old">$2,700</span></div>
              <div className="compare-line"><span className="compare-label">Concessions (1 mo free)</span><span className="compare-val compare-val-old">$1,800</span></div>
              <div className="compare-line"><span className="compare-label">NSPIRE compliance</span><span className="compare-val compare-val-old">Not included</span></div>
            </div>
            <div className="compare-col compare-new" data-testid="compare-moovin">
              <h3 className="compare-heading compare-heading-new">Moovin Forward</h3>
              <div className="compare-line"><span className="compare-label">Activation Capital</span><span className="compare-val compare-val-new">$1,100</span></div>
              <div className="compare-line"><span className="compare-label">Stabilization Fee (per unit)</span><span className="compare-val compare-val-new">$2,160</span></div>
              <div className="compare-line"><span className="compare-label">Management fees</span><span className="compare-val compare-val-new">$0</span></div>
              <div className="compare-line"><span className="compare-label">Vacancy loss (18 days)</span><span className="compare-val compare-val-new">$1,080</span></div>
              <div className="compare-line"><span className="compare-label">Concessions</span><span className="compare-val compare-val-new">Reduced</span></div>
              <div className="compare-line"><span className="compare-label">NSPIRE/HQS</span><span className="compare-val compare-val-new">Included</span></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="gate-box" data-testid="gate-box">
          <h3 className="gate-title">See the Full Line-Item Breakdown</h3>
          <p className="gate-sub">Enter your email to unlock the detailed cost comparison — where every dollar goes and why.</p>
          <form onSubmit={handleUnlock} className="gate-form" data-testid="gate-form">
            <input type="email" placeholder="Your email *" required value={gateEmail} onChange={e => setGateEmail(e.target.value)} className="gate-input" data-testid="gate-email" />
            <input type="text" placeholder="Name (optional)" value={gateName} onChange={e => setGateName(e.target.value)} className="gate-input" data-testid="gate-name" />
            <input type="text" placeholder="Company / Property (optional)" value={gateCompany} onChange={e => setGateCompany(e.target.value)} className="gate-input" data-testid="gate-company" />
            <button type="submit" className="gate-btn" disabled={gateLoading} data-testid="gate-submit">{gateLoading ? "Unlocking..." : "Unlock Full Report"}</button>
          </form>
        </div>
      )}
    </div>
  </section>
  );
};

// Service Tiers — Clean, No Prices
const ServiceTiersSection = () => {
  const ref = useReveal();
  const lang = useLang();
  return (
    <section id="services" className="service-tiers-section reveal-section" ref={ref} data-testid="service-tiers-section">
      <div className="section-inner">
        <h2 className="section-h2">{T.tiers_h2[lang]}</h2>
        <div className="tiers-grid tiers-grid-four">
          <div className="tier-card" data-testid="tier-swift-card">
            <span className="tier-timeline">48 Hours</span>
            <h3>{T.tier_swift[lang]}</h3>
            <p>{T.tier_swift_desc[lang]}</p>
            <a href="#contact" className="tier-cta">{T.tier_cta[lang]}</a>
          </div>
          <div className="tier-card tier-card-featured" data-testid="tier-standard-card">
            <span className="tier-badge">{T.tier_most_common[lang]}</span>
            <span className="tier-timeline">14 Days</span>
            <h3>{T.tier_standard[lang]}</h3>
            <p>{T.tier_standard_desc[lang]}</p>
            <a href="#contact" className="tier-cta">{T.tier_cta[lang]}</a>
          </div>
          <div className="tier-card" data-testid="tier-heavy-card">
            <span className="tier-timeline">21 Days</span>
            <h3>{T.tier_heavy[lang]}</h3>
            <p>{T.tier_heavy_desc[lang]}</p>
            <a href="#contact" className="tier-cta">{T.tier_cta[lang]}</a>
          </div>
          <div className="tier-card tier-card-admin" data-testid="tier-admin-card">
            <span className="tier-badge tier-badge-addon">{T.tier_addon_label[lang]}</span>
            <span className="tier-timeline">Per Engagement</span>
            <h3>{T.tier_admin[lang]}</h3>
            <p>{T.tier_admin_desc[lang]}</p>
            <a href="#contact" className="tier-cta">{T.tier_cta[lang]}</a>
          </div>
        </div>
        <p className="tiers-note">{T.tiers_note[lang]}</p>
      </div>
    </section>
  );
};

// Section 6: ROI Stabilization Model + Pricing + Guarantee
const PricingSection = () => {
  const ref = useReveal();
  return (
  <section id="pricing" className="pricing-section reveal-section" ref={ref} data-testid="pricing-section">
    <div className="section-inner">
      <h2 className="section-h2">The ROI Stabilization Model</h2>
      <p className="section-lead">Capital deployment flows directly into your physical asset — materials, labor, coordination. Our stabilization fee is a locked per-unit rate agreed at walk-through, due within 72 hours of sign-off on the completed deliverable.</p>

      <div className="tier-block" data-testid="tier-unified">
        <div className="breakdown-table">
          <div className="breakdown-header">
            <span>Service</span>
            <span>Timeline</span>
            <span>Fee Structure</span>
          </div>
          <div className="breakdown-row" data-testid="tier-swift">
            <span className="breakdown-unit">Swift Turn</span>
            <span className="breakdown-val">48 Hours</span>
            <span className="breakdown-val">Locked per-unit fee</span>
          </div>
          <div className="breakdown-row" data-testid="tier-volume">
            <span className="breakdown-unit">Standard Stabilization</span>
            <span className="breakdown-val">14 Days</span>
            <span className="breakdown-val">Locked per-unit fee</span>
          </div>
          <div className="breakdown-row" data-testid="tier-heavy">
            <span className="breakdown-unit">Heavy Recovery</span>
            <span className="breakdown-val">21 Days</span>
            <span className="breakdown-val">Locked per-unit fee</span>
          </div>
          <div className="breakdown-row" data-testid="tier-admin-row">
            <span className="breakdown-unit">Inspection Prep (Add-On)</span>
            <span className="breakdown-val">Per Engagement</span>
            <span className="breakdown-val">Locked admin fee</span>
          </div>
        </div>
        <div className="portfolio-note" data-testid="portfolio-note">Studio through 4BR+. Portfolio contracts available — <a href="#contact" style={{color: 'var(--cyan)'}}>contact us</a></div>
      </div>

      <div className="pilot-callout" data-testid="pilot-callout">
        <h3>First Project? Start Here.</h3>
        <p>Pilot Program for asset owners evaluating our physical recovery capability prior to portfolio engagement. Client provides materials per our capital deployment list. We execute to full institutional inspection standards. Payment structure and full standards disclosed under executive brief at walk-through.</p>
        <a href="#contact" className="pilot-cta">Learn About the Pilot Program</a>
      </div>

      <div className="guarantee-card" data-testid="guarantee-card">
        <p className="guarantee-text" data-testid="guarantee-text">Every engagement leaves with an executive-grade delivery packet and institutional inspection documentation — protection that carries into court if occupancy disputes arise. Interior-only stabilization. Every dollar accounted for.</p>
      </div>
    </div>
  </section>
  );
};

// Why Owners Choose This + Text CTA
const CloserSection = () => {
  const ref = useReveal();
  return (
  <section className="closer-section reveal-section" ref={ref} data-testid="closer-section">
    <div className="section-inner">
      <div className="text-cta-banner" data-testid="text-cta">
        <h3 className="text-cta-h3">Got a vacant unit?</h3>
        <p className="text-cta-sub">Text us the address — we'll send you a free assessment within 24 hours.</p>
        <a href={`sms:${TEXT_NUMBER}`} className="text-cta-btn" data-testid="text-cta-btn">Text {PHONE_NUMBER}</a>
      </div>
    </div>
  </section>
  );
};

// Section 6: Contact — CTA + Form
const ContactSection = () => {
  const ref = useReveal();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', message: '',
    company: '', role: '', property_address: '',
    portfolio_size: '', units_needing_service: '', service_type: '',
    vacant_units_now: '', access_available: '', pressure_source: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${BACKEND_URL}/api/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
    } catch {}
    setSubmitted(true);
    setSubmitting(false);
  };
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <section id="contact" className="contact-section reveal-section" ref={ref} data-testid="contact-section">
      <div className="section-inner">
        <div className="contact-grid">
          <div className="contact-left">
            <h2 className="contact-h2">Restore your rent roll.</h2>
            <p className="contact-sub">No long-term commitments. A complimentary on-site assessment, scope-locked engagement, and executive-grade delivery schedule — no obligation.</p>
            <div className="contact-actions">
              <PopupButton url={CALENDLY_URL} rootElement={document.getElementById("root")} text="Schedule a Visit" className="btn-primary-lg" data-testid="contact-calendly-btn" />
              <a href={`tel:${PHONE_NUMBER}`} className="btn-outline-lg">Call {PHONE_NUMBER}</a>
              <a href={`sms:${TEXT_NUMBER}`} className="btn-outline-lg">Text Us</a>
            </div>
            <p className="contact-area" data-testid="contact-area">Serving the entire <strong>DFW Metroplex</strong> — Fort Worth, Arlington, Grand Prairie, Irving, Dallas, Plano, Frisco, McKinney, Denton, Mansfield, Burleson, Haltom City, North Richland Hills, Keller, Southlake & surrounding areas.</p>
          </div>
          <div className="contact-right">
            {submitted ? (
              <div className="success-box" data-testid="success-message">
                <span className="success-check">{"\u2713"}</span>
                <h3>Inquiry received.</h3>
                <p>A member of our team will review your submission within 24 hours. If your portfolio aligns with our active engagement capacity, we will schedule a complimentary on-site assessment and issue an executive-grade scope with delivery cadence. No obligation.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form contact-form-pro" data-testid="contact-form">
                <p className="contact-form-intro" data-testid="contact-form-intro">
                  Moovin Forward operates on a <strong style={{color:'#00FFFF'}}>continuous stabilization model</strong>. Upon contract execution, your inventory enters our staggered delivery wave — rent-ready units restore to the rent roll on a daily cadence until your portfolio is stabilized. Please provide your portfolio details below to scope the engagement.
                </p>

                <div className="contact-form-row">
                  <input type="text" name="name" placeholder="Full Name *" value={formData.name} onChange={handleChange} required data-testid="name-input" />
                  <input type="text" name="company" placeholder="Company / Firm" value={formData.company} onChange={handleChange} data-testid="company-input" />
                </div>

                <div className="contact-form-row">
                  <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleChange} required data-testid="email-input" />
                  <input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleChange} data-testid="phone-input" />
                </div>

                <select name="role" value={formData.role} onChange={handleChange} data-testid="role-select" required>
                  <option value="">Your role *</option>
                  <option value="owner">Property Owner</option>
                  <option value="asset_manager">Asset Manager / REIT</option>
                  <option value="regional">Regional / VP of Operations</option>
                  <option value="broker">Broker</option>
                  <option value="receiver">Court-Appointed Receiver</option>
                  <option value="ha_procurement">Housing Authority Procurement</option>
                  <option value="pm_company">Property Management Company</option>
                  <option value="other">Other</option>
                </select>

                <input type="text" name="property_address" placeholder="Asset name / property address" value={formData.property_address} onChange={handleChange} data-testid="property-address-input" />

                <div className="contact-form-row">
                  <select name="portfolio_size" value={formData.portfolio_size} onChange={handleChange} data-testid="portfolio-size-select">
                    <option value="">Portfolio scale</option>
                    <option value="1">1 asset</option>
                    <option value="2-10">2-10 assets</option>
                    <option value="11-50">11-50 assets</option>
                    <option value="51-200">51-200 assets</option>
                    <option value="200+">200+ assets</option>
                  </select>
                  <input type="number" name="units_needing_service" placeholder="Units in scope" value={formData.units_needing_service} onChange={handleChange} min="0" data-testid="units-input" />
                </div>

                <select name="service_type" value={formData.service_type} onChange={handleChange} data-testid="service-type-select">
                  <option value="">Service tier</option>
                  <option value="swift_turn">Swift Turn Engagement</option>
                  <option value="full_stabilization">Full Stabilization Engagement</option>
                  <option value="heavy_recovery">Heavy Recovery Engagement</option>
                  <option value="nspire_section8">Inspection compliance review</option>
                  <option value="not_sure">Mixed — defer to recommendation</option>
                </select>

                <div className="contact-form-row">
                  <input type="number" name="vacant_units_now" placeholder="Current vacancy count" value={formData.vacant_units_now} onChange={handleChange} min="0" data-testid="vacant-now-input" />
                  <input type="text" name="access_available" placeholder="Earliest site access date" value={formData.access_available} onChange={handleChange} data-testid="access-input" />
                </div>

                <select name="pressure_source" value={formData.pressure_source} onChange={handleChange} data-testid="pressure-select">
                  <option value="">Primary catalyst</option>
                  <option value="lender">Lender deadline</option>
                  <option value="court">Court order / receivership</option>
                  <option value="auditor">Institutional compliance review</option>
                  <option value="capital">Capital partner mandate</option>
                  <option value="kpi">Internal KPI / quarter-end target</option>
                  <option value="just_done">Discretionary — quality-focused</option>
                </select>

                <textarea name="message" placeholder="Additional context (asset names, lender, capital partner, governing court, audit milestones)" value={formData.message} onChange={handleChange} rows="3" data-testid="message-textarea"></textarea>

                <button type="submit" className="btn-primary form-submit" disabled={submitting} data-testid="submit-btn">
                  {submitting ? "Submitting..." : "Request Walk-Through Assessment"}
                </button>
                <p className="contact-form-trust" data-testid="contact-form-trust">Confidential review. No long-term commitments. Response within 24 hours.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ===== 404 / Catch-All Page =====
// Friendly fallback for any unmatched route (e.g. /field with no projectId,
// typos, deep-link with stale path). NO dead-ends — every wrong URL still
// gives Shannon a way home.
const NotFoundPage = () => {
  const location = useLocation();
  const attempted = location.pathname + (location.search || "");
  return (
    <div className="app">
      <nav className="nav-container">
        <div className="nav-content">
          <div className="nav-logo"><Link to="/"><img src={LOGO_URL} alt="Moovin Forward" className="logo-image" /></Link></div>
          <div className="nav-links"></div>
          <div className="nav-right"><Link to="/" className="nav-link">Back to Home</Link></div>
        </div>
      </nav>
      <div className="privacy-page" data-testid="not-found-page" style={{ maxWidth: 720, textAlign: "center" }}>
        <h1 style={{ fontSize: "2.4rem", marginBottom: "0.6rem" }}>Page not found</h1>
        <p className="privacy-updated" data-testid="not-found-attempted">
          The address <code style={{ background: "#f3efe2", padding: "2px 8px", borderRadius: 3 }}>{attempted}</code> didn't match a page.
        </p>
        <p style={{ marginTop: "1.5rem", marginBottom: "2rem", color: "#4a4a4a" }}>
          No worries — happens to everybody. Here are the live front doors:
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.9rem", marginTop: "1rem" }}>
          <Link to="/" className="nav-link" style={{ padding: "1rem", border: "1px solid #d8d2bf", borderRadius: 4, textDecoration: "none" }} data-testid="nf-home">🏠 Home</Link>
          <Link to="/dashboard" className="nav-link" style={{ padding: "1rem", border: "1px solid #d8d2bf", borderRadius: 4, textDecoration: "none" }} data-testid="nf-dashboard">🛠 Team Dashboard</Link>
          <Link to="/portal" className="nav-link" style={{ padding: "1rem", border: "1px solid #d8d2bf", borderRadius: 4, textDecoration: "none" }} data-testid="nf-portal">📂 Client Portal</Link>
          <Link to="/design-studio" className="nav-link" style={{ padding: "1rem", border: "1px solid #d8d2bf", borderRadius: 4, textDecoration: "none" }} data-testid="nf-studio">🎨 Design Studio</Link>
          <Link to="/crew" className="nav-link" style={{ padding: "1rem", border: "1px solid #d8d2bf", borderRadius: 4, textDecoration: "none" }} data-testid="nf-crew">👷 Crew Portal</Link>
          <Link to="/va-contracts" className="nav-link" style={{ padding: "1rem", border: "1px solid #d8d2bf", borderRadius: 4, textDecoration: "none" }} data-testid="nf-va">🎖️ VA Contracts</Link>
        </div>
        <p style={{ marginTop: "2rem", color: "#6b7280", fontSize: "0.9rem" }}>
          Looking for <strong>Field Mode</strong>? It launches from inside the <Link to="/dashboard">Team Dashboard</Link> — pick a project first, then open Field Mode.
        </p>
      </div>
    </div>
  );
};

// Footer
const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="footer-inner">
      <div className="footer-grid">
        <div className="footer-col">
          <img src={LOGO_URL} alt="Moovin Forward" className="footer-logo" data-testid="footer-logo" />
          <p className="footer-tagline">Institutional Interior Stabilization<br />DFW Metroplex</p>
        </div>
        <div className="footer-col">
          <h4 className="footer-heading">Contact</h4>
          <a href={`tel:${PHONE_NUMBER}`} className="footer-link">{PHONE_NUMBER}</a>
          <a href={`sms:${TEXT_NUMBER}`} className="footer-link">Text Us</a>
          <a href="mailto:nivoomforward@gmail.com" className="footer-link">nivoomforward@gmail.com</a>
          <a href="https://www.linkedin.com/in/shannon-ware" className="footer-link footer-linkedin" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin">LinkedIn — Shannon Ware</a>
        </div>
        <div className="footer-col">
          <h4 className="footer-heading">Hours</h4>
          <p className="footer-text">Monday - Friday: 8am - 6pm</p>
          <p className="footer-text">Saturday: By appointment</p>
          <p className="footer-text">Sunday: Closed</p>
        </div>
        <div className="footer-col">
          <h4 className="footer-heading">Service Area</h4>
          <p className="footer-text">Fort Worth, Dallas, Arlington, Grand Prairie, Irving, Plano, Frisco & surrounding areas</p>
        </div>
        <div className="footer-col">
          <h4 className="footer-heading">Portal</h4>
          <Link to="/login" className="footer-link" data-testid="footer-portal-link">Client Portal</Link>
          <Link to="/dashboard" className="footer-link" data-testid="footer-dashboard-link">Team Dashboard</Link>
          <Link to="/design-studio" className="footer-link" data-testid="footer-studio-link">Design Studio</Link>
          <Link to="/blog" className="footer-link" data-testid="footer-blog-link">Blog</Link>
          <Link to="/portfolio" className="footer-link" data-testid="footer-portfolio-link">Our Work</Link>
          <Link to="/intake" className="footer-link" data-testid="footer-intake-link">Submit Property</Link>
          <Link to="/va-contracts" className="footer-link" data-testid="footer-va-contracts-link">VA Contracts</Link>
          <Link to="/request-packet" className="footer-link" data-testid="footer-request-packet-link">Request NDA Packet</Link>
        </div>
      </div>
      <div className="footer-bottom">
        <p className="footer-mission" data-testid="footer-mission">Creating jobs. Pushing love. Building community. One unit at a time.</p>
        <div className="footer-disclosures" data-testid="footer-disclosures">
          <p><strong>TREC & Scope Disclosure.</strong> Moovin Forward LLC is a professional make-ready, turnover, and asset stabilization firm operating exclusively on the physical condition of vacant units. We are NOT a licensed real estate brokerage, property management company, apartment locator, leasing agency, or tenant placement service. We do not represent owners or tenants in any real estate transaction. We do not market units for lease, negotiate lease terms, collect rent, process applications, conduct tenant screening, perform background or credit checks, or influence occupancy decisions in any way. We collect no tenant personal data. All leasing, marketing, and occupancy decisions remain 100% the authority of the Property Owner and their licensed representatives. <strong>Marketing Consent:</strong> By engaging Moovin Forward, Client grants a non-exclusive license to photograph, film, and publish completed work product (before/after imagery, material installations, design executions) across our website, portfolio, and social media, with the property identified at Client's written direction. Inquiries about leasing availability received through our channels are redirected to the Property Owner or their licensed agent. Fully insured. <Link to="/privacy" className="footer-policy-link">Privacy Policy</Link></p>
        </div>
        <div className="footer-proprietary" data-testid="footer-proprietary">
          <p><strong>Proprietary Notice.</strong> All methodologies, language, and frameworks on this site are protected trade secrets of Moovin Forward LLC. Unauthorized reproduction, imitation, or commercial use will be prosecuted. Full IP terms included in all client agreements.</p>
        </div>
        <div className="footer-tax-disclaimer" data-testid="footer-tax-disclaimer">
          <p><strong>Tax Disclaimer.</strong> Moovin Forward is not a tax advisor. References on this site to IRS Section 168(k), bonus depreciation, Personal Property classification, and Year-1 tax savings are informational only. Depreciation classifications and bonus depreciation eligibility depend on your individual tax situation, current tax law, and the property&rsquo;s use. Estimates shown by our calculators are illustrative and do not constitute tax advice. <strong>Consult your CPA before claiming any deduction.</strong> Moovin Forward provides receipt-level documentation and Cost Segregation-ready audit trails to support your CPA&rsquo;s analysis; we do not perform the analysis ourselves.</p>
        </div>
        <p className="footer-copy">&copy; 2026 Moovin Forward LLC. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Privacy Policy
const PrivacyPage = () => (
  <div className="app">
    <nav className="nav-container"><div className="nav-content"><div className="nav-logo"><Link to="/"><img src={LOGO_URL} alt="Moovin Forward" className="logo-image" /></Link></div><div className="nav-links"></div><div className="nav-right"><Link to="/" className="nav-link">Back to Home</Link></div></div></nav>
    <div className="privacy-page" data-testid="privacy-page">
      <h1>Privacy Policy</h1>
      <p className="privacy-updated">Last updated: March 2026</p>
      <h2>Information We Collect</h2>
      <p>When you submit a contact form, we collect your name, email address, phone number, and message. This information is used solely to respond to your inquiry and provide our services.</p>
      <h2>How We Use Your Information</h2>
      <p>We use your contact information to respond to inquiries, schedule appointments, provide project updates, and communicate about our services. We do not sell, trade, or share your personal information with third parties.</p>
      <h2>Client Portal</h2>
      <p>Our client portal uses Google authentication. We store your name, email, and profile picture to provide portal access. Project data, updates, and documents are stored securely and accessible only to you and our team.</p>
      <h2>Contact</h2>
      <p>For questions about this policy, contact us at <a href="mailto:nivoomforward@gmail.com">nivoomforward@gmail.com</a> or call <a href="tel:+18173818831">(817) 381-8831</a>.</p>
    </div>
  </div>
);

// Mobile sticky call bar
const MobileCallBar = () => (
  <div className="mobile-call-bar" data-testid="mobile-call-bar">
    <a href={`tel:${PHONE_NUMBER}`} className="mobile-call-link" data-testid="mobile-call-link">
      <span className="mobile-call-icon">&#9742;</span>
      <span className="mobile-call-number">{PHONE_NUMBER}</span>
    </a>
    <a href={`sms:${TEXT_NUMBER}`} className="mobile-text-link" data-testid="mobile-text-link">Text Us</a>
  </div>
);

// Main Page
const MainPage = () => {
  const [lang, setLang] = useState("en");

  // If a logged-in CEO/COO/admin hits "/", quietly redirect to /dashboard
  // so "View Preview" lands her on the dashboard. Prospects see the
  // marketing page immediately with zero flash. Append ?marketing=1 to
  // force the landing page even when logged in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("marketing") === "1") return;
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : null))
      .then(user => {
        if (cancelled || !user) return;
        const role = (user.dashboard_role || user.role || "").toLowerCase();
        // RBAC routing — 3 lanes:
        //   admin/ceo/coo → full COO dashboard
        //   crew/team/1099 → Field Worker View (jobs, tools, materials, capture)
        //   owner/property_owner → Property Owner view (falls through to dashboard,
        //     which has its own owner-role tab visibility)
        if (role === "crew" || role === "team" || role === "1099" || role === "field") {
          window.location.replace("/field");
        } else if (role === "ceo" || role === "coo" || role === "admin" || role === "administrator") {
          window.location.replace("/dashboard");
        } else if (role === "owner" || role === "property_owner" || role === "landlord") {
          window.location.replace("/owner-vault");
        } else if (user && (user.email || user.user_id)) {
          // Fail-safe: authenticated user but unknown role → send to dashboard
          // (which has its own role-filter tabs). Prevents the "black screen"
          // that used to appear when a fresh Google sign-in had no dashboard_role.
          window.location.replace("/dashboard");
        }
        // Truly anonymous users land on the public marketing site.
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <LangContext.Provider value={lang}>
      <div className="app">
        <Navigation lang={lang} setLang={setLang} />
        <MobileCallBar />
        <main>
          <HeroSection />
          <ValueSection />
          <TaxShieldSection />
          <ServiceTiersSection />
          <TrustSection />
          <ContactSection />
        </main>
        <Footer />
      </div>
    </LangContext.Provider>
  );
};

// Blog Page
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [activePost, setActivePost] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/blog`).then(r => r.json()).then(setPosts).catch(() => {});
  }, []);
  return (
    <div className="app">
      <Navigation />
      <div className="blog-page" data-testid="blog-page">
        <div className="section-inner">
          <h1 className="blog-title">Moovin Forward — Insights</h1>
          <p className="blog-subtitle">DFW property turnover strategies, market analysis, and compliance guides.</p>
          <div className="blog-connect"><a href="https://www.linkedin.com/in/shannon-ware" target="_blank" rel="noopener noreferrer" className="blog-linkedin-btn" data-testid="blog-linkedin">Follow Shannon Ware on LinkedIn</a></div>
          {!activePost ? (
            <div className="blog-grid">
              {posts.map(p => (
                <div key={p.slug} className="blog-card" onClick={() => setActivePost(p)} data-testid={`blog-card-${p.slug}`}>
                  <span className="blog-card-cat">{p.category}</span>
                  <h2>{p.title}</h2>
                  <p>{p.excerpt}</p>
                  <span className="blog-card-date">{p.published}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="blog-post" data-testid="blog-post">
              <button onClick={() => setActivePost(null)} className="blog-back-btn">&larr; All Articles</button>
              <span className="blog-post-cat">{activePost.category}</span>
              <h2>{activePost.title}</h2>
              <span className="blog-post-date">{activePost.published}</span>
              <div className="blog-post-body">{activePost.content.split('\n\n').map((para, i) => <p key={i}>{para.startsWith('**') ? <strong>{para.replace(/\*\*/g, '')}</strong> : para}</p>)}</div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Portfolio Page
const PortfolioPage = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/portfolio`).then(r => r.ok ? r.json() : []).then(setItems).catch(() => {});
  }, []);
  return (
    <div className="app">
      <Navigation />
      <div className="portfolio-page" data-testid="portfolio-page">
        <div className="section-inner">
          <h1>Our Work</h1>
          <p className="portfolio-intro">Before & after transformations across DFW. Every unit delivered market-ready with full NSPIRE documentation.</p>
          {items.length === 0 ? (
            <div className="portfolio-coming-soon">
              <h2>Portfolio Coming Soon</h2>
              <p>Active engagement portfolio in development. Project showcases — including before/after documentation, stabilization timelines, and client engagements — will be published upon completion of our first delivery wave.</p>
              <p>Interested in becoming a featured launch engagement? <Link to="/#contact" className="portfolio-cta">Inquire about our Pilot Program.</Link></p>
            </div>
          ) : (
            <div className="portfolio-grid">
              {items.map(item => (
                <div key={item.portfolio_id} className="portfolio-card" data-testid={`portfolio-${item.portfolio_id}`}>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  {item.days_to_complete > 0 && <span className="portfolio-days">{item.days_to_complete} days to complete</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Testimonial Submit Page
const TestimonialPage = () => {
  const [form, setForm] = useState({ name: "", company: "", text: "", rating: 5 });
  const [submitted, setSubmitted] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await fetch(`${BACKEND_URL}/api/testimonials`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setSubmitted(true);
  };
  return (
    <div className="app">
      <Navigation />
      <div className="testimonial-page" data-testid="testimonial-page">
        <div className="section-inner">
          <h1>Share Your Experience</h1>
          {submitted ? (
            <div className="testimonial-thanks"><h2>Thank you!</h2><p>Your testimonial has been submitted and will appear on our site after review.</p><Link to="/" className="testimonial-home-btn">Back to Home</Link></div>
          ) : (
            <form onSubmit={submit} className="testimonial-form" data-testid="testimonial-form">
              <input type="text" placeholder="Your Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="testimonial-input" data-testid="testimonial-name" />
              <input type="text" placeholder="Company (optional)" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} className="testimonial-input" />
              <div className="testimonial-rating">
                <span>Rating:</span>
                {[1,2,3,4,5].map(r => <button key={r} type="button" onClick={() => setForm(f => ({...f, rating: r}))} className={`rating-star ${form.rating >= r ? "rating-active" : ""}`}>&#9733;</button>)}
              </div>
              <textarea placeholder="Tell us about your experience..." value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} required rows={5} className="testimonial-input" data-testid="testimonial-text" />
              <button type="submit" className="testimonial-submit" data-testid="testimonial-submit">Submit Testimonial</button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Client Intake Form (Public)
const IntakePage = () => {
  const location = useLocation();
  const roleParam = new URLSearchParams(location.search).get("role") || "";
  const isSiteExecutor = ["property_manager","leasing_agent","maintenance_lead","lead_maintenance","field_contractor"].includes(roleParam);
  const roleLabel = {
    property_manager: "Property Manager",
    leasing_agent: "Leasing Agent",
    maintenance_lead: "Lead Maintenance",
    lead_maintenance: "Lead Maintenance",
    field_contractor: "Field Contractor",
  }[roleParam] || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", unit_type: "2br_1ba", description: "", role: roleParam, quick_prompt: "" });
  const [submitted, setSubmitted] = useState(false);
  const [lang, setLang] = useState("en");
  const submit = async (e) => {
    e.preventDefault();
    await fetch(`${BACKEND_URL}/api/intake`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setSubmitted(true);
  };
  return (
    <div className="app">
      <Navigation />
      <div className="intake-page" data-testid="intake-page">
        <div className="section-inner">
          {isSiteExecutor && (
            <div data-testid={`intake-role-banner-${roleParam}`}
              style={{ background: "rgba(0,207,207,0.08)", border: "1px solid rgba(0,207,207,0.35)", padding: "12px 16px", borderRadius: 8, marginBottom: 16, color: "#00CFCF", fontSize: 13 }}>
              <strong>Site Executor Access · {roleLabel}</strong> — scope-bound intake. Data routes direct to the owner ledger.
              Tier-1 Owner Vault is not accessible from this session.
            </div>
          )}
          <div className="lang-toggle">
            <button onClick={() => setLang("en")} className={`lang-btn ${lang === "en" ? "active" : ""}`}>English</button>
            <button onClick={() => setLang("es")} className={`lang-btn ${lang === "es" ? "active" : ""}`}>Espanol</button>
          </div>
          <h1>{lang === "es" ? "Envie Su Propiedad" : "Submit Your Property"}</h1>
          <p className="intake-intro">{lang === "es" ? "Cuentenos sobre su propiedad y nos comunicaremos dentro de 24 horas con un plan personalizado." : "Tell us about your property and we'll reach out within 24 hours with a custom plan."}</p>
          {submitted ? (
            <div className="testimonial-thanks" data-testid="intake-thanks"><h2>{lang === "es" ? "Recibido!" : "Received!"}</h2><p>{lang === "es" ? "Nos comunicaremos dentro de 24 horas." : "We'll reach out within 24 hours."}</p><Link to="/" className="testimonial-home-btn">{lang === "es" ? "Volver al Inicio" : "Back to Home"}</Link></div>
          ) : (
            <form onSubmit={submit} className="intake-form" data-testid="intake-form">
              <input type="text" placeholder={lang === "es" ? "Nombre Completo" : "Full Name"} value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required className="testimonial-input" data-testid="intake-name" />
              <input type="email" placeholder={lang === "es" ? "Correo Electronico" : "Email"} value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required className="testimonial-input" data-testid="intake-email" />
              <input type="tel" placeholder={lang === "es" ? "Telefono" : "Phone"} value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="testimonial-input" />
              <input type="text" placeholder={lang === "es" ? "Direccion de la Propiedad" : "Property Address"} value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} required className="testimonial-input" data-testid="intake-address" />
              <select value={form.unit_type} onChange={e => setForm(f => ({...f, unit_type: e.target.value}))} className="testimonial-input">
                <option value="1br_1ba">1 BR / 1 BA</option>
                <option value="2br_1ba">2 BR / 1 BA</option>
                <option value="2br_2ba">2 BR / 2 BA</option>
                <option value="3br_2ba">3 BR / 2 BA</option>
                <option value="multi">{lang === "es" ? "Multiple Unidades" : "Multiple Units"}</option>
              </select>
              {isSiteExecutor && (
                <input type="text" placeholder="Quick prompt · what needs to be addressed?" value={form.quick_prompt}
                  onChange={e => setForm(f => ({...f, quick_prompt: e.target.value}))} className="testimonial-input" data-testid="intake-quick-prompt" />
              )}
              <textarea placeholder={lang === "es" ? "Describa la condicion y lo que necesita..." : "Describe the condition and what you need..."} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={4} className="testimonial-input" data-testid="intake-description" />
              <button type="submit" className="testimonial-submit" data-testid="intake-submit">{lang === "es" ? "Enviar" : "Submit Property"}</button>
            </form>
          )}
          {isSiteExecutor && !submitted && (
            <OnsiteGuidedWalkthrough role={roleParam} propertyAddress={form.address} />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <SiteErrorBoundary>
        <Toaster
          position="top-center"
          richColors
          closeButton
          toastOptions={{
            style: {
              background: "rgba(3,11,26,0.98)",
              border: "1px solid rgba(0,255,255,0.35)",
              color: "#fff",
              fontFamily: "'Inter', sans-serif",
            },
          }}
        />
        {/* Global scope-lock floating overlay — appears anywhere in the
            app the moment DynamicMaterialsBuilder dispatches
            `mf:scope-lock`. No manual tab-hop, no sign-in gate, no
            handshake. Per automation directive Feb 12, 2026. */}
        <ScopeLockRender variant="floating" />

        {/* Global PWA Install Prompt — banner appears on EVERY page
            so first-time visitors (prospects, Shannon's family, expo
            attendees) can pin the app to their home screen from
            wherever they land. Chrome fires the native prompt; iOS
            Safari shows the "Tap Share → Add to Home Screen"
            tooltip. Auto-hides when already installed. */}
        <InstallPrompt />
        <MobileFieldNav />
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/design-studio" element={<DesignStudio />} />
          <Route path="/desire-studio" element={<DesireStudioPreview />} />
          <Route path="/desire-studio-preview" element={<DesireStudioPreview />} />
          <Route path="/ceo-field" element={<CEOField />} />
          <Route path="/field-mode" element={<CEOField />} />
          <Route path="/inspect" element={<FieldInspection />} />
          <Route path="/partner/:partnerId" element={<PartnerWelcome />} />
          <Route path="/clear-unit/:token" element={<ClearUnitRestart />} />
          <Route path="/render-hub/:inspectionId" element={<RenderHub />} />
          <Route path="/hub/:token" element={<RenderHub publicMode />} />
          <Route path="/gateway" element={<Gateway />} />
          <Route path="/partner/:partnerId/onboarded" element={<PartnerWelcome />} />
          <Route path="/partner/:partnerId/refresh" element={<PartnerWelcome />} />
          <Route path="/inspect/:inspectionId" element={<FieldInspection />} />
          <Route path="/expo" element={<ExpoLive />} />
          <Route path="/expo-live" element={<ExpoLive />} />
          <Route path="/mobile" element={<CrewMobile />} />
          <Route path="/mobile/:project_id" element={<CrewMobile />} />
          <Route path="/fwaa-2026" element={<FWAA />} />
          <Route path="/fwaa" element={<FWAA />} />
          <Route path="/fwa-2026" element={<FWAA />} />
          <Route path="/fwa" element={<FWAA />} />
          <Route path="/fwa-qr" element={<FWAQR />} />
          <Route path="/qr-fwa" element={<FWAQR />} />
          <Route path="/fwa/qr" element={<FWAQR />} />
          <Route path="/crew" element={<CrewPortal />} />
          <Route path="/field/:projectId" element={<FieldMode launchMode="admin" />} />
          <Route path="/crew/:token" element={<FieldMode launchMode="crew" />} />
          <Route path="/vendor-consent/:token" element={<VendorConsent />} />
          <Route path="/sign/:sigId" element={<SignContract />} />
          <Route path="/trigger/:token" element={<OwnerTriggerView />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/testimonial" element={<TestimonialPage />} />
          <Route path="/intake" element={<IntakePage />} />
          <Route path="/va-contracts" element={<VAContracts />} />
          <Route path="/request-packet" element={<RequestPacket />} />
          <Route path="/vendor-disclaimer" element={<VendorDisclaimer />} />
          <Route path="/vendor-application" element={<VendorApplication />} />
          <Route path="/apply" element={<VendorApplication />} />
          <Route path="/join" element={<PartnerIntake />} />
          <Route path="/partners/intake" element={<PartnerIntake />} />
          <Route path="/partners/join" element={<PartnerIntake />} />
          <Route path="/work" element={<VendorApplication />} />
          <Route path="/activate" element={<ActivationGate />} />
          <Route path="/activation-gate" element={<ActivationGate />} />
          <Route path="/owner-vault" element={<OwnerShieldVault />} />
          <Route path="/field-specialist" element={<FieldSpecialist />} />
          <Route path="/shayla" element={<FieldSpecialist />} />
          <Route path="/vendor-portal/:workOrderId" element={<VendorPortal />} />
          <Route path="/design-preview" element={<DesignStudioPreview />} />
          <Route path="/lockdown-status" element={<LockdownStatusPage />} />
          <Route path="/simulation" element={<PipelineSimulation />} />
          <Route path="/get-app" element={<GetAppPage />} />
          <Route path="/install" element={<GetAppPage />} />
          <Route path="/roster-admin" element={<RosterAdmin />} />
          <Route path="/coo-command-center" element={<CooCommandCenter />} />
          <Route path="/coo" element={<CooCommandCenter />} />
          <Route path="/my-qr" element={<MyQR />} />
          <Route path="/qr" element={<MyQR />} />
          <Route path="/proposal/:token" element={<OwnerProposal />} />
          {/* Friendly redirects for URLs Shannon's likely to type/save */}
          <Route path="/field" element={<CrewPortal />} />
          <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          {/* Catch-all 404 — keeps every wrong URL from being a dead-end */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </SiteErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
